import { _isNotEmpty } from '@/littledash';
import { type Nullable } from '@/model/Common.model';

/**
 * Normalizes timepoint values from various formats into a string array.
 * Handles Handsontable's different storage formats (arrays, CSV strings, null/undefined).
 *
 * @param value - The value to convert (array, string, null, undefined, etc.)
 * @returns Array of trimmed string values
 */
export const valueToArray = (value: Nullable<any>): Array<string> => {
  if (Array.isArray(value)) {
    return value.reduce((acc, v) => {
      switch (typeof v) {
        case 'string':
          return [...acc, v.trim()];
        case 'number':
          return [...acc, `${v}`];
        default:
          return acc;
      }
    }, []);
  }
  if (typeof value === 'string') {
    return value.split(',').reduce<Array<string>>((acc, v) => {
      const result = v.trim();
      if (_isNotEmpty(result)) {
        acc.push(result);
      }
      return acc;
    }, []);
  }
  return [];
};

export const sortDesc = (a: number, b: number): number => b - a;

export const sortAsc = (a: number, b: number): number => a - b;

export const sortDescByProperty =
  <T, K extends keyof T>(property: K) =>
  (a: T, b: T): number =>
    (b[property] as any) - (a[property] as any);

export const sortAscByProperty =
  <T, K extends keyof T>(property: K) =>
  (a: T, b: T): number =>
    (a[property] as any) - (b[property] as any);

export const sortDescByDateStrProperty =
  <T, K extends keyof T>(property: K) =>
  (a: T, b: T): number =>
    new Date(b[property] as any).getTime() - new Date(a[property] as any).getTime();

export const sortAscByDateStrProperty =
  <T, K extends keyof T>(property: K) =>
  (a: T, b: T): number =>
    new Date(a[property] as any).getTime() - new Date(b[property] as any).getTime();

export const getRandomElement = <T>(a: Array<T>): T => {
  return a[Math.floor(a.length * Math.random())];
};

/**
 * Compares two arrays for equality using a custom comparator function.
 * Includes fast-path optimizations for reference equality and length mismatch.
 * Short-circuits on the first element that fails the comparison.
 *
 * @param a - First array to compare
 * @param b - Second array to compare
 * @param comparator - Function to compare individual elements
 * @returns true if arrays are equal according to the comparator, false otherwise
 *
 * @example
 * arraysEqual([1, 2, 3], [1, 2, 3], (x, y) => x === y) // true
 * arraysEqual(['a', 'b'], ['A', 'B'], (x, y) => x.toLowerCase() === y.toLowerCase()) // true
 * arraysEqual([{id: 1}], [{id: 1}], (x, y) => x.id === y.id) // true
 */
export const arraysEqual = <T>(a: Array<T>, b: Array<T>, comparator: (x: T, y: T) => boolean): boolean => {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((item, i) => comparator(item, b[i]));
};
